import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from "recharts";

const DoughnutChart = ({ chartData, title = "Distribution" }) => {
  const [activeIndex, setActiveIndex] = useState(-1);

  if (!Array.isArray(chartData) || chartData.length === 0) {
    return (
      <div style={{ height: "260px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span style={{ color: "#8c8c8c", fontSize: "14px" }}>No data available</span>
      </div>
    );
  }

  // Calculate total count
  const total = chartData.reduce((acc, curr) => acc + (curr.count || 0), 0);

  // Modern Premium Colors
  const COLORS = [
    "#4f46e5", // Indigo
    "#ec4899", // Rose Pink
    "#10b981", // Emerald
    "#f59e0b", // Amber
    "#06b6d4", // Cyan
    "#8b5cf6", // Violet
    "#ef4444", // Coral Red
  ];

  // Process data with percentages
  const data = chartData.map((item, index) => {
    const count = item.count || 0;
    const percentage = total > 0 ? ((count / total) * 100).toFixed(1) : "0.0";
    return {
      name: item._id || "Unknown",
      value: count,
      percentage,
      color: COLORS[index % COLORS.length],
    };
  });

  // Custom active shape for hover state
  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    return (
      <g>
        {/* Main hovered segment (expanded slightly) */}
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 5}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        {/* Outer glowing ring */}
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={outerRadius + 8}
          outerRadius={outerRadius + 12}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          opacity={0.25}
        />
      </g>
    );
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(-1);
  };

  return (
    <div
      className="modern-doughnut-layout"
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "24px",
        minHeight: "260px",
        padding: "8px 0",
        flexWrap: "wrap",
      }}
    >
      {/* Chart Section */}
      <div
        style={{
          width: "200px",
          height: "200px",
          position: "relative",
          flexShrink: 0,
          margin: "0 auto",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              activeIndex={activeIndex}
              activeShape={renderActiveShape}
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={68}
              outerRadius={80}
              dataKey="value"
              onMouseEnter={onPieEnter}
              onMouseLeave={onPieLeave}
              stroke="#ffffff"
              strokeWidth={2}
              paddingAngle={2}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>

            {/* Center Static/Dynamic Text */}
            <text x="50%" y="50%" textAnchor="middle" dominantBaseline="middle">
              <tspan
                x="50%"
                dy="-8"
                fontSize="10px"
                fontWeight="700"
                fill="#8c8c8c"
                style={{ letterSpacing: "0.08em" }}
              >
                TOTAL
              </tspan>
              <tspan
                x="50%"
                dy="22"
                fontSize="22px"
                fontWeight="800"
                fill="#141414"
              >
                {total}
              </tspan>
            </text>
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Interactive Legend Section */}
      <div
        style={{
          flex: 1,
          minWidth: "200px",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        {data.map((item, idx) => {
          const isHovered = activeIndex === idx;
          return (
            <div
              key={item.name}
              onMouseEnter={() => setActiveIndex(idx)}
              onMouseLeave={() => setActiveIndex(-1)}
              style={{
                display: "flex",
                flexDirection: "column",
                padding: "6px 12px",
                borderRadius: "8px",
                backgroundColor: isHovered ? "rgba(240, 242, 245, 0.7)" : "transparent",
                border: "1px solid",
                borderColor: isHovered ? "rgba(0, 0, 0, 0.05)" : "transparent",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "4px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: item.color,
                      boxShadow: isHovered ? `0 0 8px ${item.color}` : "none",
                      transition: "box-shadow 0.2s ease",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: isHovered ? "600" : "500",
                      color: isHovered ? "#141414" : "#595959",
                      textTransform: "capitalize",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {item.name.toLowerCase().replace(/_/g, " ")}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#262626",
                  }}
                >
                  {item.value}{" "}
                  <span style={{ fontSize: "11px", fontWeight: "normal", color: "#8c8c8c" }}>
                    ({item.percentage}%)
                  </span>
                </span>
              </div>

              {/* Custom Linear Progress Bar */}
              <div
                style={{
                  width: "100%",
                  height: "4px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${item.percentage}%`,
                    height: "100%",
                    backgroundColor: item.color,
                    borderRadius: "2px",
                    transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DoughnutChart;
